<div class="card">
  <div class="card-content">

    <!-- Stats: shared .stats-cards-row / .stats-card so this page reads like the others -->
    <div class="stats-cards-row">
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ projectCount }}</span>
          <span class="stats-card-label">Projects</span>
          <span class="stats-card-sublabel">{{ distinctMemberCount }} team members</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-folder"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatHoursCompact(totalAllocatedHours) }}</span>
          <span class="stats-card-label">Allocated</span>
          <span class="stats-card-sublabel">
            {{ report?.monthCount ?? 0 }} month{{ (report?.monthCount ?? 0) === 1 ? '' : 's' }}
            &times; {{ report?.standardHoursPerDay ?? 8 }}h days
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-calendar"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatHoursCompact(totalLoggedHours) }}</span>
          <span class="stats-card-label">Logged</span>
          <span class="stats-card-sublabel">Leave excluded</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-clock"></i></div>
      </div>

      <div class="stats-card"
           [class.stats-card--danger]="totalVarianceHours > 0"
           [class.stats-card--warning]="totalVarianceHours < 0"
           [class.stats-card--success]="totalVarianceHours === 0">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatVarianceCompact(totalVarianceHours) }}</span>
          <span class="stats-card-label">Variance</span>
          <span class="stats-card-sublabel">Logged &minus; allocated</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-chart-line"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatPercentage(overallUtilization) }}</span>
          <span class="stats-card-label">Utilization</span>
          <span class="stats-card-sublabel">Logged as % of plan</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-percentage"></i></div>
      </div>
    </div>

    <!-- Filters: shared .filter-bar, shown/hidden by the main header filter toggle -->
    <div class="filter-bar filter-bar-collapsible mb-4"
         [class.filter-bar-visible]="pageHeaderService.currentFiltersVisible">

      <!-- One shared .filter-bar-filters, as every other page uses. It wraps, and a
           full-width spacer after the period fields forces the break there, so the four
           date selects stay together as one thought instead of splitting mid-way. -->
      <div class="filter-bar-filters pur-filter-row">
        <div class="filter-field">
          <label for="pur-start-year" class="filter-label">From Year</label>
          <p-select id="pur-start-year" [options]="yearOptions" [(ngModel)]="startYear"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-start-month" class="filter-label">From Month</label>
          <p-select id="pur-start-month" [options]="monthOptions" [(ngModel)]="startMonth"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-end-year" class="filter-label">To Year</label>
          <p-select id="pur-end-year" [options]="yearOptions" [(ngModel)]="endYear"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-end-month" class="filter-label">To Month</label>
          <p-select id="pur-end-month" [options]="monthOptions" [(ngModel)]="endMonth"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <!-- Pushed to the right of the period row: these three are controls rather than
             filters — what counts, how it is drawn, and a reset — so they sit apart from the
             fields that decide which data is shown. -->
        <div class="pur-row-actions">
          <div class="filter-field pur-filter-toggle">
            <label for="pur-drafts" class="filter-label">Include Drafts</label>
            <div class="pur-toggle-cell">
              <p-toggleSwitch inputId="pur-drafts" [(ngModel)]="includeDrafts"
                              (onChange)="onFilterChange()"
                              pTooltip="Count hours from work logs that have not been submitted yet"
                              tooltipPosition="top" />
            </div>
          </div>

          <div class="filter-field pur-filter-view">
            <label class="filter-label">View</label>
            <div class="pur-view-switch" role="group" aria-label="View mode">
              <button type="button" class="pur-view-btn"
                      [class.pur-view-btn--active]="viewMode === 'table'"
                      [attr.aria-pressed]="viewMode === 'table'"
                      (click)="setViewMode('table')"
                      pTooltip="Show the project table" tooltipPosition="top">
                <i class="pi pi-table" aria-hidden="true"></i> Table
              </button>
              <button type="button" class="pur-view-btn"
                      [class.pur-view-btn--active]="viewMode === 'chart'"
                      [attr.aria-pressed]="viewMode === 'chart'"
                      (click)="setViewMode('chart')"
                      pTooltip="Compare allocated against logged hours" tooltipPosition="top">
                <i class="pi pi-chart-bar" aria-hidden="true"></i> Chart
              </button>
            </div>
          </div>

          <!-- Rolls the filtered projects up by PM, client or service group. A dialog rather
               than a third view, because it answers a different question from the table and
               is read alongside it rather than instead of it. -->
          <div class="filter-field pur-filter-summary">
            <label class="filter-label">&nbsp;</label>
            <button type="button" class="btn-summary-view" (click)="openSummaryDialog()"
                    pTooltip="Totals grouped by PM, client or service group"
                    tooltipPosition="top">
              <i class="pi pi-chart-pie" aria-hidden="true"></i>
              Summary
            </button>
          </div>

          <div class="filter-field pur-filter-clear" *ngIf="hasActiveFilters">
            <label class="filter-label">&nbsp;</label>
            <button type="button" class="btn-clear-filters" (click)="clearFilters()"
                    pTooltip="Clear all filters" tooltipPosition="top" title="Clear all filters">
              <i class="pi pi-filter-slash"></i>
            </button>
          </div>
        </div>

        <!-- Zero-height, full-width: forces the wrap here so the period fields and their
             controls keep one line and the narrowing filters start the next. -->
        <div class="pur-row-break" aria-hidden="true"></div>

        <div class="filter-field">
          <label for="pur-pm" class="filter-label">PM</label>
          <p-multiSelect id="pur-pm" [options]="pmOptions" [(ngModel)]="selectedPMs"
                         optionLabel="label" optionValue="value" placeholder="All PMs"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search PM..."
                         (ngModelChange)="onProjectFilterChange()"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-project" class="filter-label">Project</label>
          <p-multiSelect id="pur-project" [options]="projectOptions" [(ngModel)]="selectedProjectIds"
                         optionLabel="label" optionValue="value" placeholder="All projects"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search project..."
                         (ngModelChange)="onProjectFilterChange()"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-type" class="filter-label">Project Type</label>
          <p-select id="pur-type" [options]="projectTypeOptions" [(ngModel)]="projectTypeFilter"
                    optionLabel="label" optionValue="value"
                    (ngModelChange)="onProjectFilterChange()" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-invoice" class="filter-label">Invoice Type</label>
          <p-multiSelect id="pur-invoice" [options]="invoiceTypeOptions" [(ngModel)]="selectedInvoiceTypes"
                         optionLabel="label" optionValue="value" placeholder="Invoice Type"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true"
                         (ngModelChange)="onProjectFilterChange()"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-cost-center" class="filter-label">Cost Center</label>
          <p-multiSelect id="pur-cost-center" [options]="costCenterOptions" [(ngModel)]="selectedCostCenterIds"
                         optionLabel="label" optionValue="value" placeholder="All Cost Centers"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search cost center..."
                         (ngModelChange)="onProjectFilterChange()"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pur-service-group" class="filter-label">Service Group</label>
          <p-multiSelect id="pur-service-group" [options]="serviceGroupOptions" [(ngModel)]="selectedServiceGroupIds"
                         optionLabel="label" optionValue="value" placeholder="All service groups"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search service group..."
                         (ngModelChange)="onProjectFilterChange()"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

      </div>
    </div>

    <!-- An impossible range is guidance, not an error: say what to fix and fetch nothing. -->
    <div class="pur-range-error" *ngIf="rangeError">
      <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
      <span>{{ rangeError }}</span>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="loading-container loading-container--centered">
      <i class="pi pi-spin pi-spinner loading-spinner-icon"></i>
      <p>Loading project utilization report...</p>
    </div>

    <ng-container *ngIf="!loading && report && !rangeError">

      <div class="pur-chart-panel" *ngIf="viewMode === 'chart'">
        <div class="pur-chart-toolbar">
          <!-- The chart draws one row per project, so a cap keeps the bars thick enough to
               compare; All is there when the whole picture is wanted. -->
          <div class="pur-chart-topn">
            <label for="pur-topn" class="pur-chart-topn-label">Show</label>
            <p-select id="pur-topn" [options]="chartTopNOptions" [(ngModel)]="chartTopN"
                      optionLabel="label" optionValue="value"
                      (ngModelChange)="onChartTopNChange()" appendTo="body" />
          </div>
        </div>

        <p class="pur-chart-caption" *ngIf="chartIsTruncated">
          Showing the {{ chartProjectCount }} projects with the most allocated hours, of
          {{ projectCount }}. Choose <strong>All</strong> above, or filter, to chart a
          different set.
        </p>
        <div class="pur-chart-scroll">
          <div class="pur-chart-holder" [style.height]="chartHeight">
            <p-chart type="bar" [data]="chartData" [options]="chartOptions"
                     [style]="{ height: '100%', width: '100%' }" />
          </div>
        </div>
      </div>

      <p-table *ngIf="viewMode === 'table'"
               [value]="projects" styleClass="pur-table resize-handles-visible"
               [tableStyle]="{ 'min-width': '100%' }" [paginator]="false" [scrollable]="true"
               scrollHeight="flex" sortMode="multiple"
               [resizableColumns]="true" columnResizeMode="expand"
               persistColumnWidths="pur-main">

        <ng-template pTemplate="header">
          <tr>
            <!-- No resizer on the expand toggle: a grip would crowd an icon-width column. -->
            <th class="pur-th-toggle"></th>
            <th pResizableColumn [pSortableColumn]="'projectName'">
              Project <p-sortIcon field="projectName" />
            </th>
            <th pResizableColumn [pSortableColumn]="'clientName'">
              Client <p-sortIcon field="clientName" />
            </th>
            <th pResizableColumn [pSortableColumn]="'managerName'">
              PM <p-sortIcon field="managerName" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'memberCount'">
              Members <p-sortIcon field="memberCount" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'allocatedHours'">
              Allocated <p-sortIcon field="allocatedHours" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'loggedHours'">
              Logged <p-sortIcon field="loggedHours" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'varianceHours'">
              Variance <p-sortIcon field="varianceHours" />
            </th>
            <!-- Last column: dragging the final border has nothing to push against. -->
            <th class="text-right" [pSortableColumn]="'utilizationPercentage'"
                pTooltip="Logged hours as a percentage of allocated hours."
                tooltipPosition="top">
              Utilization <p-sortIcon field="utilizationPercentage" />
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-project>
          <!-- Project total -->
          <tr class="pur-project-row">
            <td class="pur-td-toggle">
              <button type="button" class="pur-toggle-btn"
                      *ngIf="project.members.length"
                      (click)="toggleRow(project.projectId)"
                      [attr.aria-expanded]="isExpanded(project.projectId)"
                      [pTooltip]="isExpanded(project.projectId) ? 'Hide members' : 'Show members'"
                      tooltipPosition="top">
                <i class="pi"
                   [class.pi-chevron-right]="!isExpanded(project.projectId)"
                   [class.pi-chevron-down]="isExpanded(project.projectId)"></i>
              </button>
            </td>
            <td>
              <div class="pur-project-name">{{ project.projectName }}</div>
              <div class="pur-project-sub" *ngIf="project.memberCount">
                {{ project.memberCount }} member{{ project.memberCount === 1 ? '' : 's' }}
              </div>
            </td>
            <td>{{ project.clientName || '—' }}</td>
            <td>{{ project.managerName || '—' }}</td>
            <td class="text-right">{{ project.memberCount }}</td>
            <td class="text-right">{{ formatHours(project.allocatedHours) }}</td>
            <td class="text-right">{{ formatHours(project.loggedHours) }}</td>
            <td class="text-right pur-variance"
                [class.pur-variance-over]="project.varianceHours > 0"
                [class.pur-variance-under]="project.varianceHours < 0">
              {{ formatHours(project.varianceHours) }}
            </td>
            <td class="text-right">
              <span class="pur-util" [ngClass]="utilizationClass(project.utilizationPercentage)">
                {{ formatPercentage(project.utilizationPercentage) }}
              </span>
            </td>
          </tr>

          <!-- Member detail -->
          <ng-container *ngIf="isExpanded(project.projectId)">
            <tr *ngFor="let member of project.members" class="pur-member-row">
              <td></td>
              <td>
                <div class="pur-member-cell">
                  <span class="pur-member-name">{{ member.employeeName }}</span>
                  <span class="pur-member-role" *ngIf="member.roleTitle">
                    {{ member.roleTitle }}
                  </span>
                </div>
              </td>
              <td></td>
              <td></td>
              <td class="text-right pur-member-months"
                  [pTooltip]="'Months with planned time on this project'" tooltipPosition="top">
                {{ member.monthsAllocated || '—' }}
              </td>
              <td class="text-right">{{ formatHours(member.allocatedHours) }}</td>
              <td class="text-right">{{ formatHours(member.loggedHours) }}</td>
              <td class="text-right pur-variance"
                  [class.pur-variance-over]="member.varianceHours > 0"
                  [class.pur-variance-under]="member.varianceHours < 0">
                {{ formatHours(member.varianceHours) }}
              </td>
              <td class="text-right">
                <span class="pur-util" [ngClass]="utilizationClass(member.utilizationPercentage)">
                  {{ formatPercentage(member.utilizationPercentage) }}
                </span>
              </td>
            </tr>
          </ng-container>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr class="table-empty-row">
            <td colspan="9" class="table-empty-cell">
              <div class="empty-state">
                <div class="empty-icon"><i class="pi pi-inbox"></i></div>
                <p class="empty-message">
                  {{ hasActiveFilters ? 'No matching projects' : 'No project activity for this period' }}
                </p>
                <p class="empty-description">
                  {{ hasActiveFilters
                      ? 'Try clearing the search or looking for a different project or member'
                      : 'No allocations or work logs were found for ' + (report.periodLabel || 'this period') }}
                </p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

    </ng-container>

    <div *ngIf="!loading && !report && !rangeError" class="empty-state">
      <i class="pi pi-exclamation-circle"></i>
      <span>Report could not be loaded.</span>
    </div>

  </div>
</div>

<!-- Summary dialog. Reuses the shared .unallocated-employees-dialog styles the workforce and
     not-allocated popups use, so the three read as one pattern. -->
<p-dialog
  [visible]="showSummaryDialog"
  (visibleChange)="showSummaryDialog = $event"
  [modal]="true"
  styleClass="dialog-workforce dialog-workforce--steady unallocated-employees-dialog"
  [dismissableMask]="true"
  [closable]="true">

  <ng-template pTemplate="header">
    <div class="dialog-header-with-meta pur-summary-header">
      <span class="dialog-header-title">Summary</span>
      <span class="dialog-header-badge">{{ summaryRows.length }}</span>
      <span class="dialog-header-period">{{ report?.periodLabel }}</span>

      <!-- Controls sit in the header rather than in a bar above the table: there are only
           two, and the dialog's one job is this list. -->
      <span class="pur-summary-tools">
        <input type="text" class="pur-summary-input" [(ngModel)]="summarySearch"
               placeholder="Search project..." aria-label="Search project name" />

        <p-select class="pur-summary-min" [options]="summaryMinHoursOptions"
                  [(ngModel)]="summaryMinAllocatedHours" optionLabel="label"
                  optionValue="value" appendTo="body"
                  pTooltip="Show only projects above this many allocated hours"
                  tooltipPosition="bottom" />

        <button type="button" class="pur-summary-reset" *ngIf="hasSummaryFilters"
                (click)="clearSummaryFilters()"
                pTooltip="Clear these filters" tooltipPosition="bottom"
                aria-label="Clear these filters">
          <i class="pi pi-filter-slash"></i>
        </button>
      </span>
    </div>
  </ng-template>

  <div class="unallocated-dialog-inner">
    <!-- These narrow the summary only. The page's own filters already apply, since the rows
         are built from the same filtered project list. -->
    <div class="unallocated-table-scroll-holder">
      <p-table [value]="summaryRows"
               styleClass="compact-striped-table unallocated-dialog-table resize-handles-visible"
               [paginator]="false" sortMode="multiple"
               [resizableColumns]="true" columnResizeMode="expand"
               persistColumnWidths="pur-summary">
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn pSortableColumn="name">
              Project Name <p-sortIcon field="name" />
            </th>
            <th pResizableColumn class="text-right" pSortableColumn="allocatedHours">
              Allocated <p-sortIcon field="allocatedHours" />
            </th>
            <th pResizableColumn class="text-right" pSortableColumn="loggedHours">
              Utilized <p-sortIcon field="loggedHours" />
            </th>
            <!-- Last column: no resizer, nothing to push against. -->
            <th class="text-right" pSortableColumn="utilizationPercentage">
              Utilization <p-sortIcon field="utilizationPercentage" />
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-row>
          <tr>
            <td><span class="unallocated-cell-text" [pTooltip]="row.name"
                      tooltipPosition="top">{{ row.name }}</span></td>
            <td class="text-right">{{ formatHours(row.allocatedHours) }}</td>
            <td class="text-right">{{ formatHours(row.loggedHours) }}</td>
            <td class="text-right">
              <span class="pur-util" [ngClass]="utilizationClass(row.utilizationPercentage)">
                {{ formatPercentage(row.utilizationPercentage) }}
              </span>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr class="table-empty-row table-empty-row--compact">
            <td colspan="4" class="table-empty-cell">
              <div class="empty-state empty-state--compact">
                <div class="empty-icon"><i class="pi pi-inbox"></i></div>
                <p class="empty-message">No matching projects</p>
                <p class="empty-description">
                  {{ hasSummaryFilters
                      ? 'Try a different search or lower the minimum hours'
                      : 'No projects for the selected period and filters' }}
                </p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="workforce-footer">
      <span class="workforce-footer-summary">
        <strong>{{ formatHoursCompact(summaryAllocatedTotal) }}</strong> allocated
        <span class="workforce-footer-sep">&middot;</span>
        <strong>{{ formatHoursCompact(summaryLoggedTotal) }}</strong> utilized
        <span class="workforce-footer-sep">&middot;</span>
        <strong>{{ formatPercentage(summaryUtilizationTotal) }}</strong> utilization
      </span>
      <span class="workforce-footer-actions">
        <p-button label="Close" icon="pi pi-times"
                  (onClick)="closeSummaryDialog()" severity="secondary" />
        <p-button label="Export Excel" icon="pi pi-file-excel"
                  (onClick)="exportSummaryToExcel()" severity="success" />
      </span>
    </div>
  </ng-template>
</p-dialog>
